<template>
  <div class="Xpage_container Xcontent-box">
    <ReportStatStaticCard
      :points="meta.reports"
      :metrics="metrics"
      :selected-index="currentSelection"
      @onClick="changeSelection"
    />
    <section class="campaign_filters">
      <div class="campaign_filter">
        <SearchInput
          v-model="searchValue"
          :placeholder="$t('CAMPAIGN.FILTERS.SEARCH_PLACEHOLDER')"
          @search="onSearch"
        />
      </div>
      <div class="campaign_filter filters__button-wrap">
        <div v-if="appliedFilters.length" class="filters__applied-indicator" />
        <woot-button
          class="margin-right-small clear"
          color-scheme="secondary"
          data-testid="create-new-contact"
          icon="filter"
          @click="onToggleFilter"
        >
          {{ $t('CONTACTS_PAGE.FILTER_CONTACTS') }}
        </woot-button>
      </div>
    </section>

    <section class="campaign_filters">
      <div class="campaign_filter">
        <p class="input_label">{{ $t('ORDER.LIST_TABLE.DATE.LABEL') }}</p>
        <date-range-picker
          :confirm-text="$t('ORDER.LIST_TABLE.DATE_SELECT_CONFIRM_BTN')"
          :placeholder="$t('ORDER.LIST_TABLE.DATE.PLACEHOLDER')"
          class="font_normal order_start_date"
          :value="dateRange"
          :disabled-date="disableFilterDate"
          @change="handleDateChange"
        />
      </div>
    </section>
    <!-- report UI -->
    <div class="table-container">
      <CallLogsTable
        :is-loading="uiFlags.isFetching"
        :records="records"
        :meta="meta"
        :group-by="groupBy"
        @on-sort-change="onSortChange"
        @change-group-by="onChangeGroupBy"
        @on-row-click="onRowClick"
      />
    </div>

    <!-- Call Log Detail Modal -->
    <CallLogDetailModal
      :is-open="showDetailsModal"
      :call-log="callLogDetails"
      :is-loading="isLoadingCallLogDetails"
      @onClose="onModalClose"
    />

    <div class="loadMore_btn">
      <woot-button
        variant="clear"
        size="small"
        color-scheme="primary"
        :is-loading="uiFlags.isFetching"
        :is-disabled="paginationCompute.isLastPage"
        @click="onPageChange(Number(meta.current_page) + 1)"
      >
        {{ paginationCompute.btnText }}
      </woot-button>
    </div>

    <woot-modal
      :show.sync="showFiltersModal"
      :on-close="onToggleFilter"
      size="medium"
    >
      <LLMReportAdvancedFilter
        v-if="showFiltersModal"
        :on-close="onToggleFilter"
        :filter-items="filterItems"
        :initial-applied-filters="appliedFilters"
        :filter-attributes="filterAttributes"
        @applyFilter="onApplyFilter"
        @clearFilters="clearFilters"
      />
    </woot-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';
import DateRangePicker from 'components/ui/DateRangePicker.vue';
import moment from 'moment';
import helperMixin from 'shared/mixins/helperMixin';
import SearchInput from 'dashboard/components/ui/SearchInput.vue';
import {
  llmReportFilterTypes,
  llmReportFilterAttributList,
} from '../broadcastAgent/aiCampaignConstant';
import ReportStatStaticCard from 'dashboard/routes/dashboard/settings/campaigns/broadcastAgent/ReportStatStaticCard.vue';
import LLMReportAdvancedFilter from './LLMReportAdvancedFilter.vue';
import CallLogsTable from '../outboundCallCampaign/CallLogsTable.vue';
import CallLogDetailModal from '../outboundCallCampaign/CallLogDetailModal.vue';
import InteractionHistoryApi from 'dashboard/api/interaction_histories.js';

export default {
  components: {
    DateRangePicker,
    CallLogsTable,
    SearchInput,
    ReportStatStaticCard,
    LLMReportAdvancedFilter,
    CallLogDetailModal,
  },
  mixins: [helperMixin, alertMixin],
  data() {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - 1);

    return {
      dateRange: [startDate, endDate],
      currentSelection: null,
      searchValue: '',
      selectedStatus: null,
      sortBy: null,
      call_direction: null,
      kind: null,
      sortConfig: { name: 'asc' },
      groupBy: 'call',
      showFiltersModal: false,
      filterItems: llmReportFilterTypes.map(filter => ({
        ...filter,
        attributeName: this.$t(`${filter.attributeI18nKey}`),
      })),
      appliedFilters: [],
      showDetailsModal: false,
      callLogDetails: null,
      isLoadingCallLogDetails: false,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'interactionHistories/getUIFlags',
      meta: 'interactionHistories/getInteractionHistoriesMeta',
      records: 'interactionHistories/getInteractionHistories',
    }),
    paginationCompute() {
      const pageSize = this.meta.page_size || 10;
      const totalPages = Math.ceil(this.meta.count / pageSize);
      const currentPage = Number(this.meta.current_page);
      const isLastPage = currentPage >= totalPages;
      const currentCount =
        Math.min(currentPage * pageSize, this.meta.count) || 0;

      // const btnText = `Load More Orders (${currentCount}/${this.meta.count})`;
      const btnText = `${this.$t(
        'CAMPAIGN.LLM_CHATBOT.AI_BROADCAST_LIST.AGENT_CALL_REPORT.LOAD_MORE_BTN'
      )} (${currentCount}/${this.meta.count})`;

      return {
        isLastPage,
        btnText,
      };
    },
    metrics() {
      const customMetrics = [
        {
          NAME:
            'CAMPAIGN.LLM_CHATBOT.AI_BROADCAST_LIST.CALL_CAMPAIGN.REPORT.TOTAL_CALL',
          KEY: 'total_call',
          DESC: '( total )',
        },
        {
          NAME:
            'CAMPAIGN.LLM_CHATBOT.AI_BROADCAST_LIST.CALL_CAMPAIGN.REPORT.TOTAL_DURATION',
          KEY: 'total_call_duration',
          DESC: '( total )',
        },
        {
          NAME:
            'CAMPAIGN.LLM_CHATBOT.AI_BROADCAST_LIST.CALL_CAMPAIGN.REPORT.TOTAL_INBOUND',
          KEY: 'total_inbound_call',
          DESC: '( total )',
        },
        {
          NAME:
            'CAMPAIGN.LLM_CHATBOT.AI_BROADCAST_LIST.CALL_CAMPAIGN.REPORT.TOTAL_OUTBOUND',
          KEY: 'total_outbound_call',
          DESC: '( total )',
        },
      ];
      return customMetrics.map(el => ({ ...el, NAME: this.$t(el.NAME) }));
    },
    filterAttributes() {
      return llmReportFilterAttributList.map(el => ({
        ...el,
        name: this.$t(el.name),
      }));
    },
  },
  watch: {
    groupBy(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.getQueryData({ page: 1 });
      }
    },
  },
  activated() {
    this.getQueryData({
      start_date: this.formatStartEndDate(this.dateRange[0], this.dateRange[1])
        .start_date,
      end_date: this.formatStartEndDate(this.dateRange[0], this.dateRange[1])
        .end_date,
    });
  },
  methods: {
    async getQueryData(newQuery = {}) {
      try {
        const defautQuery = {
          page: this.meta.current_page,
          page_size: this.meta.page_size,
          q: this.searchValue,
          payload: this.appliedFilters,
          result: this.selectedStatus?.id,
          sort: this.getSortAttribute(this.sortConfig),
          order: this.sortBy?.order,
          direction: this.sortBy?.direction,
          call_direction: this.call_direction?.id,
          kind: this.kind?.id,
          group: this.groupBy === 'contact' ? 'contact' : undefined,
          start_date: this.formatStartEndDate(
            this.dateRange[0],
            this.dateRange[1]
          ).start_date,
          end_date: this.formatStartEndDate(
            this.dateRange[0],
            this.dateRange[1]
          ).end_date,
          ...newQuery,
        };

        if (defautQuery.payload.length) {
          delete defautQuery.payload[defautQuery.payload.length - 1]
            .query_operator;
        }

        if (defautQuery.payload?.length) {
          this.$store.dispatch(
            'interactionHistories/fetchInteractionHistoriesFiltered',
            this.cleanObject(defautQuery)
          );
        } else {
          delete defautQuery.payload;
          this.$store.dispatch(
            'interactionHistories/fetchInteractionHistories',
            this.cleanObject(defautQuery)
          );
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    },
    async handleDateChange(dateRange) {
      this.dateRange = dateRange;
      const date = this.formatStartEndDate(dateRange[0], dateRange[1]);
      this.getQueryData({
        page: 1,
        start_date: date.start_date,
        end_date: date.end_date,
      });
    },
    disableFilterDate(date) {
      const today = moment();
      const monthAgo = 6;
      const nMonthsAgo = moment().subtract(monthAgo, 'months'); // disable before 3 months
      const targetDate = moment(date);
      return (
        targetDate.isAfter(today, 'day') ||
        targetDate.isBefore(nMonthsAgo, 'day')
      );
    },
    onPageChange(page) {
      this.getQueryData({ page });
    },
    onSearch(value) {
      this.getQueryData({ q: value, page: 1 });
    },
    changeSelection(index) {
      if (this.currentSelection === index) {
        this.currentSelection = null;
      } else {
        this.currentSelection = index;
      }
    },
    onToggleFilter() {
      this.showFiltersModal = !this.showFiltersModal;
    },
    onApplyFilter(filters) {
      this.appliedFilters = filters;
      this.getQueryData({ payload: filters, page: 1 });
      this.onToggleFilter();
    },
    clearFilters() {
      this.appliedFilters = [];
      this.getQueryData({ payload: [], page: 1 });
    },
    getSortAttribute(newSort = {}) {
      let sortAttr = Object.keys({ ...this.sortConfig, ...newSort }).reduce(
        (acc, sortKey) => {
          const sortOrder = this.sortConfig[sortKey];
          if (sortOrder) {
            const sortOrderSign = sortOrder === 'asc' ? '' : '-';
            return `${sortOrderSign}${sortKey}`;
          }
          return acc;
        },
        ''
      );
      if (!sortAttr) {
        this.sortConfig = { name: 'asc' };
        sortAttr = 'name';
      }
      return sortAttr;
    },
    onSortChange(params) {
      let sortDetails = {};
      Object.keys(params).forEach(key => {
        if (params[key] !== '') {
          sortDetails = {};
          sortDetails.key = key;
          sortDetails.order = params[key];
        }
      });
      this.sortConfig = params;
      const sort = this.getSortAttribute(sortDetails);
      this.getQueryData({ sort, page: 1 });
    },
    onChangeGroupBy(value) {
      this.groupBy = value;
    },
    async onRowClick(rowId) {
      try {
        this.isLoadingCallLogDetails = true;
        this.showDetailsModal = true;
        const {
          data,
        } = await InteractionHistoryApi.fetchAgentInteactionHistoryByLLMid(
          rowId
        );
        this.callLogDetails = data;
      } catch (error) {
        this.showAlert(
          this.$t('CAMPAIGN.LLM_CHATBOT.SINGLE_REPORT_MODAL.API_ERROR')
        );
      } finally {
        this.isLoadingCallLogDetails = false;
      }
    },
    onModalClose() {
      this.showDetailsModal = false;
      this.callLogDetails = null;
    },
  },
};
</script>

<style scoped lang="scss">
.campaign_filters {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 16px;
  .campaign_filter {
    p {
      margin-bottom: 8px;
    }
  }
}
.loadMore_btn button {
  display: block;
  margin: auto;
}

.filters__button-wrap {
  position: relative;

  .filters__applied-indicator {
    position: absolute;
    height: var(--space-small);
    width: var(--space-small);
    top: var(--space-smaller);
    right: var(--space-slab);
    background-color: var(--s-500);
    border-radius: var(--border-radius-rounded);
  }
}
</style>
